// RECORD-TYPES-1 — React access to a business's own record types.
//
// One load per sub-account, cached in the lib, shared by the nav, the generic
// list and detail pages, and the editor. Before the load resolves there are NO
// types and NO warning, so the menu renders exactly as it does today and then
// gains the business's own rows - rather than flashing an error at the many
// accounts that have none.
//
// THE HOOK RETURNS THE STATE, NOT JUST THE LIST, because a menu that cannot
// tell "you have none" from "we could not ask" would silently hide a
// business's own records from them.

import { useCallback, useEffect, useMemo, useState } from 'react'
import { activeTypes, typeBySlug, type CustomRecordType } from '../lib/recordTypes'
import {
  loadRecordTypes,
  resolveRecordTypes,
  type RecordTypesState,
  resetRecordTypesCache,
} from '../lib/recordTypesRepo'
import { useOptionalAuth } from '../providers/AuthProvider'

export interface UseRecordTypes {
  loaded: boolean
  /** True when we read successfully and the business has none. */
  none: boolean
  /** Non-null ONLY when a read failed. */
  warning: string | null
  /** Every type, removed ones included — a detail page needs those to explain
   *  a record whose type is gone. */
  all: CustomRecordType[]
  /** Active types, in the business's own order. What the menu renders. */
  active: CustomRecordType[]
  bySlug: (slug: string | undefined) => CustomRecordType | null
  reload: () => void
}

/**
 * GHOST-BUILDS-1 — THE MENU CAN BE TOLD A NEW SECTION EXISTS.
 *
 * `reload()` already existed but is per-hook-instance: the panel holds no
 * reference to the sidebar's copy, so a section Ghost created did not appear
 * until something else re-read. This is the same module-level signal
 * useAccountNavConfig uses, for the same reason and with the same constraint —
 * no provider required, because navigation must not fail for want of an
 * optional feature.
 */
const listeners = new Set<() => void>()

/**
 * Tell every mounted menu to re-read this business's record types.
 *
 * IT CLEARS THE CACHE FIRST, and that is the whole reason this needed fixing.
 * loadRecordTypes() serves from a module-level Map that only the LOCAL write
 * paths in recordTypesRepo invalidate. Ghost writes through the edge function,
 * so nothing in the browser ever cleared it: the listeners fired, the effect
 * re-ran, and it was handed the same cached answer. The section only appeared
 * after a reload — the exact defect this signal exists to prevent.
 */
export function refreshRecordTypes(): void {
  resetRecordTypesCache()
  for (const l of listeners) l()
}

export function useRecordTypes(): UseRecordTypes {
  // OPTIONAL, deliberately: the nav and a list row are often rendered in a
  // test or a shell that has not mounted the provider. No account means no
  // business to ask, which resolves to no custom types — byte-identical to
  // today. PLATFORM-1 added this seam for the same reason.
  const subAccountId = useOptionalAuth()?.currentSubAccountId ?? null
  const [state, setState] = useState<RecordTypesState>({ state: 'loading' })
  const [nonce, setNonce] = useState(0)

  useEffect(() => {
    let cancelled = false
    setState({ state: 'loading' })
    void loadRecordTypes(subAccountId).then((s) => {
      if (!cancelled) setState(s)
    })
    return () => {
      cancelled = true
    }
  }, [subAccountId, nonce])

  const reload = useCallback(() => setNonce((n) => n + 1), [])

  // Re-read whenever anything calls refreshRecordTypes().
  useEffect(() => {
    listeners.add(reload)
    return () => {
      listeners.delete(reload)
    }
  }, [reload])

  return useMemo(() => {
    const r = resolveRecordTypes(state)
    const active = activeTypes(r.types)
    return {
      loaded: r.loaded,
      none: r.none,
      warning: r.warning,
      all: r.types,
      active,
      // Looks in ALL types, not just active ones: a bookmarked url to a
      // removed type should say "this was removed", not "not found".
      bySlug: (slug: string | undefined) => typeBySlug(r.types, slug),
      reload,
    }
  }, [state, reload])
}
